/**
 * App Configuration Entity Schemas
 *
 * Types for application configuration, features, and settings.
 *
 * The `globalFeatures` and `screenFeatures` blocks are the canonical per-feature
 * and per-screen schemas defined in `./global-features` and `./screen-features`
 * (the single source of truth shared across backend, mobile, and web).
 */

import { z } from 'zod'
import { GlobalFeaturesConfigSchema } from './global-features'
import { ScreensConfigSchema } from './screen-features'

/**
 * Theme Config Schema
 */
export const ThemeConfigSchema = z.object({
  primaryColor: z.string().optional(),
  secondaryColor: z.string().optional(),
  accentColor: z.string().optional(),
  backgroundColor: z.string().optional(),
  textColor: z.string().optional(),
  logoUrl: z.string().nullable().optional(),
  logoUrlDark: z.string().nullable().optional(),
  customCss: z.string().nullable().optional()
})

export type ThemeConfig = z.infer<typeof ThemeConfigSchema>

/**
 * App Config Schema
 *
 * `globalFeatures` / `screenFeatures` use the canonical per-feature / per-screen
 * schemas. Both root objects keep Zod's default object behavior (unknown keys
 * tolerated on parse but stripped from the output), so custom feature/screen
 * keys still validate.
 */
export const AppConfigSchema = z.object({
  id: z.string().optional(),
  schoolId: z.string().optional(),
  theme: ThemeConfigSchema.optional(),
  globalFeatures: GlobalFeaturesConfigSchema.optional(),
  screenFeatures: ScreensConfigSchema.optional(),
  navigationMenus: z.record(z.string(), z.unknown()).optional(),
  updatedAt: z.string().optional()
})

export type AppConfig = z.infer<typeof AppConfigSchema>
